import type { AdminTeamRow, Season } from "@/lib/types";
import {
  groupByLineage,
  lineageKeyOf,
  lineageRepresentative,
  newestSeasonFirst,
} from "@/features/navigation/seasonGroups";

/**
 * #908 season teams (plan §6, D9, D13) — pure grouping helpers shared by the
 * club console Teams / Seasons / Overview pages and the onboarding first-team
 * step. No React, no fetches: callers pass the `/api/admin/teams` and
 * `/api/admin/seasons` payloads they already hold.
 */

export function isArchivedTeam(team: Pick<AdminTeamRow, "status">): boolean {
  return team.status === "archived";
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Seasons newest first by `starts_on` (blank dates last, then by name). */
export function sortSeasonsNewestFirst(seasons: readonly Season[]): Season[] {
  return [...seasons].sort(
    (a, b) =>
      (b.starts_on || "").localeCompare(a.starts_on || "") ||
      a.name.localeCompare(b.name),
  );
}

/** The club's current season: the default season, else the newest season
 * whose dates include today, else the newest season. `null` without any. */
export function pickCurrentSeason(
  seasons: readonly Season[],
  today = todayIso(),
): Season | null {
  const byDefault = seasons.find((season) => season.is_default);
  if (byDefault) return byDefault;
  const sorted = sortSeasonsNewestFirst(seasons);
  const current = sorted.find(
    (season) =>
      (!season.starts_on || season.starts_on <= today) &&
      (!season.ends_on || season.ends_on >= today),
  );
  return current ?? sorted[0] ?? null;
}

/** One club team across its seasons (#908 D1). A team is formed per season
 * and Carry forward links the season teams under one `lineage_key`; the
 * console lists the team once and moves between its season teams. */
export type TeamLineage = {
  key: string;
  /** Season teams, newest season first; a team from before seasons last. */
  members: AdminTeamRow[];
  /** The season team the list row opens: the newest current, else the
   * newest. */
  representative: AdminTeamRow;
  /** `current`: it has a current season team. `legacy`: its only current
   * team is from before seasons (D9). `past`: every season team is
   * archived. */
  kind: "current" | "legacy" | "past";
};

/** Teams grouped by lineage, ordered by the representative's name. */
export function groupTeamsByLineage(
  teams: readonly AdminTeamRow[],
  seasons: readonly Season[],
): TeamLineage[] {
  const rows = teams.map((row) => ({
    id: row.id,
    season: row.season_id ? { id: row.season_id, name: "" } : null,
    archived: isArchivedTeam(row),
    row,
  }));
  return groupByLineage(rows, (item) => lineageKeyOf(item.row))
    .map((members) => {
      const sorted = newestSeasonFirst(members, seasons);
      const representative = lineageRepresentative(sorted, seasons);
      const current = sorted.filter((member) => !member.archived);
      return {
        key: lineageKeyOf(representative.row),
        members: sorted.map((member) => member.row),
        representative: representative.row,
        kind: !current.length
          ? ("past" as const)
          : current.some((member) => member.season)
            ? ("current" as const)
            : ("legacy" as const),
      };
    })
    .sort((a, b) =>
      a.representative.name.localeCompare(b.representative.name, undefined, {
        sensitivity: "base",
      }),
    );
}
